import algoliasearch from 'algoliasearch/lite';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Configure, InstantSearch } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import config from '../../../config';
import Button from '../../Button/Button';
import AlgoliaLoader from '../../Loader/AlgoliaLoader';
import Modal from '../../Modal/Modal';
import ModalBody from '../../Modal/ModalBody';
import SellFromHomeMetalChips from './SellFromHomeMetalChips';
import SellFromHomeProductList from './SellFromHomeProductList';
import SellFromHomeSearchInput from './SellFromHomeSearchInput';
import SellFromHomeStepsDrawer from './SellFromHomeStepsDrawer';
import SellFromHomeWeightDropdown from './SellFromHomeWeightDropdown';

const { apiKey, apiSecret, apiSfhIndex } = config.algoliaSearch;
const searchClient = algoliasearch(apiKey, apiSecret);

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(8)};
`;

const FieldLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(13)};
  color: ${themeColors.colorLabel};
  margin: ${pxToRem(16)} 0 ${pxToRem(6)};
`;

const Heading = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorTextSecondary};
  margin: ${pxToRem(20)} 0 ${pxToRem(8)};
`;

const ListSection = styled.div`
  flex: 1;
  min-height: 0;
  max-height: ${pxToRem(280)};
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  margin-top: ${pxToRem(16)};

  > button {
    width: 100%;
  }
`;

const SellFromHomeWizard = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [isStepsDrawerOpen, setIsStepsDrawerOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  useEffect(() => {
    setIsStepsDrawerOpen(isOpen);
    if (!isOpen) {
      setSelectedCode(null);
      setIsSearchExpanded(false);
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      close={onClose}
      title={t('sellWizard.sfhWizard.title')}
      size="sm"
      maxWidth={pxToRem(500)}
      minHeight={pxToRem(576)}
      clipContent
      closeIconVisible
      skipListeningToLocations
    >
      <ModalBody>
        <InstantSearch indexName={apiSfhIndex} searchClient={searchClient}>
          <Configure hitsPerPage={20} />
          <FiltersRow>
            {!isSearchExpanded && (
              <SellFromHomeMetalChips attribute="MetalType" operator="or" />
            )}
            <SellFromHomeSearchInput
              isExpanded={isSearchExpanded}
              onToggleExpanded={() => setIsSearchExpanded((open) => !open)}
            />
          </FiltersRow>
          <FieldLabel>{t('sellWizard.sfhWizard.weightLabel')}</FieldLabel>
          <SellFromHomeWeightDropdown
            attribute="WeightCaption"
            operator="or"
            limit={50}
          />
          <Heading>{t('sellWizard.sfhWizard.productListHeading')}</Heading>
          <ListSection>
            <AlgoliaLoader>
              <SellFromHomeProductList
                selectedCode={selectedCode}
                onSelect={(hit) => setSelectedCode(hit.Code)}
              />
            </AlgoliaLoader>
          </ListSection>
          <Footer>
            <Button
              variant="primary"
              textTransform="none"
              disabled={!selectedCode}
            >
              {t('sellWizard.sfhWizard.continue')}
            </Button>
          </Footer>
        </InstantSearch>
      </ModalBody>
      <SellFromHomeStepsDrawer
        isOpen={isStepsDrawerOpen}
        onClose={() => setIsStepsDrawerOpen(false)}
      />
    </Modal>
  );
};

SellFromHomeWizard.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SellFromHomeWizard;
